import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Cross, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const { login } = useAuth();
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const pinRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
  ];

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);

    // Auto-advance to next input
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pinValue = pin.join('');
    if (pinValue.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setLoading(true);
    try {
      await login(phone.trim(), pinValue);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid phone number or PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Hero Section */}
      <div 
        className="h-[40vh] relative bg-cover bg-center"
        style={{ 
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(248,250,252,1)), url('https://images.unsplash.com/photo-1636051551573-15d6cb59db52?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')` 
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg mb-4">
            <Cross className="w-8 h-8 text-violet-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-4xl text-slate-900 tracking-tight">Kings Photos</h1>
          <p className="text-slate-600 mt-2">Capturing moments of faith</p>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 -mt-8 relative z-10">
        <div className="bg-white rounded-t-[32px] shadow-xl px-6 py-8 min-h-full">
          <form onSubmit={handleSubmit} className="max-w-sm mx-auto space-y-6">
            {/* Phone Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number
              </label>
              <Input
                data-testid="phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                className="h-14 text-lg rounded-2xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-violet-200 focus:border-violet-400"
              />
            </div>

            {/* PIN Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                4-Digit PIN
              </label>
              <div className="pin-input-container">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={pinRefs[index]}
                    data-testid={`pin-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    className="pin-input-slot"
                  />
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div data-testid="login-error" className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-xl">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-14 text-lg font-medium rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-200 transition-all hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
